/**
 * `TransferModal` — the presentational "Transfer Case" form. Collects a committed
 * recipient, one-or-more reasons, an optional note and an optional case-context note, shows the
 * read-only "Logged As" line, and calls `onSubmit(payload)`. Owns FORM state ONLY — no api/context/
 * clock; the container (`TransferAction`) owns open/close, the actual call, submit status, and the
 * `loggedAs` values passed in as props. Mirrors `HoldModal` (CDMX-18412) deliberately.
 *
 * `buildTransferPayload` (`../../slots/buildTransferPayload`) is the single source of truth for
 * validation, reused for both the submit handler and the per-field error flags below. Field errors
 * are LIVE-DERIVED every render from the CURRENT values once a submit has been attempted, so
 * editing a field clears its error immediately.
 *
 * `ModalBody` holds ONLY the description: Chakra auto-wires the dialog's `aria-describedby` to
 * `ModalBody`'s id, so the actual form renders in a sibling `Box` — otherwise the whole form would
 * become the dialog's accessible description (same trick as `HoldModal`).
 */
import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Text,
  Textarea
} from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';
import { RoundedButton } from '@codametrix/ui-components';

import type {
  TransferActionData,
  TransferPayload,
  TransferRecipient
} from '../../interface/transfer';
import {
  buildTransferPayload,
  type TransferValidationError
} from '../../slots/buildTransferPayload';
import { splitReasonOptions, toSelectedReasons } from '../../slots/splitReasonOptions';
import { CASE_ACTION_RADIUS, TRANSFER_COLORS } from '../../styles/tokens';
import { LoggedAsLine } from '../CaseActionModal';
import { ReasonField, useReasonOptions } from '../ReasonField';
import { RecipientCombobox } from '../RecipientCombobox';

/** See `HoldModal`'s note: RoundedButton forwards `isLoading` at runtime but doesn't type it. */
const AtomButton = RoundedButton as React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & { isLoading?: boolean }
>;

export type TransferSubmitState = 'idle' | 'transferring' | 'error';

export interface TransferModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly loggedAs: {
    readonly name: string;
    readonly at: Date;
    /** IANA zone — pins the footer timestamp for deterministic rendering. */
    readonly timeZone?: string;
  };
  readonly data?: TransferActionData;
  readonly onSearch?: (
    term: string
  ) => Promise<readonly TransferRecipient[]>;
  readonly onSubmit: (payload: TransferPayload) => Promise<void>;
  readonly submitState: TransferSubmitState;
  readonly errorMessage?: string;
  /**
   * Whether a transfer can actually be performed. `false` (the shipped default) renders the
   * primary action disabled.
   */
  readonly canSubmit?: boolean;
  readonly finalFocusRef?: React.RefObject<HTMLElement>;
  /** Forwarded to the combobox so tests can drive the debounce without fake timers. */
  readonly searchDebounceMs?: number;
}

const TRANSFER_MODAL_TITLE = 'Transfer Case';
/** Ticket R3. Deliberately does NOT promise a queue destination (we transfer to people only in this
 *  story) and does NOT repeat the prototype's "They'll see your note" — nothing persists a note. */
const TRANSFER_MODAL_DESCRIPTION =
  'This case will be reassigned to another person and removed from your active workload.';

const EYEBROW = {
  textStyle: 'Subtitle 1',
  color: TRANSFER_COLORS.inkSoft
} as const;

export const TransferModal: React.FC<TransferModalProps> = ({
  isOpen,
  onClose,
  loggedAs,
  data,
  onSearch,
  onSubmit,
  submitState,
  errorMessage,
  canSubmit = false,
  finalFocusRef,
  searchDebounceMs
}) => {
  const [selected, setSelected] = useState<TransferRecipient | undefined>(
    undefined
  );
  const [reasonKeys, setReasonKeys] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const [caseContext, setCaseContext] = useState('');
  const [hasAttempted, setHasAttempted] = useState(false);
  const reasonGroupRef = useRef<HTMLElement>(null);
  const recipientRef = useRef<HTMLDivElement>(null);

  // Reset ALL form state on each closed->open transition. `TransferAction` also remounts this
  // component per open (`key={openGen}`); this is defense-in-depth for any other caller.
  useEffect(() => {
    if (isOpen) {
      setSelected(undefined);
      setReasonKeys([]);
      setNote('');
      setCaseContext('');
      setHasAttempted(false);
    }
  }, [isOpen]);

  const { state: reasonState, retry } = useReasonOptions('transfer', isOpen);
  const options = reasonState.status === 'loaded' ? reasonState.options : [];
  const noReasons =
    reasonState.status !== 'loaded' ||
    splitReasonOptions(options).pills.length + splitReasonOptions(options).listOptions.length === 0;

  const busy = submitState === 'transferring';
  const values = {
    recipient: selected,
    reasons: toSelectedReasons(options, reasonKeys),
    note,
    caseContext
  };
  const liveResult = hasAttempted ? buildTransferPayload(values) : null;
  const errs: TransferValidationError[] =
    liveResult && liveResult.ok === false ? liveResult.errors : [];
  const recipientError = errs.includes('recipient-required');
  const reasonError = errs.includes('reason-required');

  const attempt = async (): Promise<void> => {
    setHasAttempted(true);
    const result = buildTransferPayload(values);
    if (result.ok === false) {
      // Focus the first invalid control: recipient -> reason group.
      if (result.errors.includes('recipient-required')) {
        recipientRef.current?.querySelector('input')?.focus();
      } else if (result.errors.includes('reason-required')) {
        reasonGroupRef.current?.focus();
      }
      return;
    }
    await onSubmit(result.payload);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={busy ? () => undefined : onClose}
      isCentered
      size="xl"
      finalFocusRef={finalFocusRef}
      closeOnOverlayClick={!busy}
      closeOnEsc={!busy}
    >
      <ModalOverlay />
      <ModalContent
        data-testid="transfer-modal"
        borderRadius={CASE_ACTION_RADIUS.modal}
        maxW="52rem"
        aria-label={TRANSFER_MODAL_TITLE}
      >
        <Flex
          align="center"
          gap={3}
          px={7}
          pt={6}
          pb={5}
          borderBottomWidth="1px"
          borderColor={TRANSFER_COLORS.border}
        >
          <Flex
            aria-hidden
            data-testid="transfer-modal-icon"
            bg={TRANSFER_COLORS.iconBg}
            borderRadius={CASE_ACTION_RADIUS.tile}
            boxSize="2.75rem"
            flexShrink={0}
            align="center"
            justify="center"
          >
            <Box
              as="svg"
              viewBox="0 0 24 24"
              width="1.4rem"
              height="1.4rem"
              fill="none"
              stroke={TRANSFER_COLORS.iconArrow}
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M7 17 17 7M9 7h8v8" />
            </Box>
          </Flex>
          <Flex direction="column" justify="center" gap="0.125rem" minW={0}>
            <Text textStyle="h3">{TRANSFER_MODAL_TITLE}</Text>
            <ModalBody p={0}>
              <Text textStyle="Body 1" color={TRANSFER_COLORS.inkMuted}>
                {TRANSFER_MODAL_DESCRIPTION}
              </Text>
            </ModalBody>
          </Flex>
        </Flex>
        <ModalCloseButton
          isDisabled={busy}
          aria-label="Close"
          top="2.125rem"
          right={7}
          bg={TRANSFER_COLORS.inputBg}
          border={`1px solid ${TRANSFER_COLORS.border}`}
          borderRadius={CASE_ACTION_RADIUS.small}
          size="sm"
          color={TRANSFER_COLORS.inkMuted}
          _hover={{ bg: TRANSFER_COLORS.closeHoverBg }}
        />

        <Box px={7} pt={5} pb={5} maxH="60vh" overflowY="auto">
          {/* PERSON */}
          <Box mb={6} ref={recipientRef}>
            <Text as="label" htmlFor="transfer-recipient-input" {...EYEBROW}>
              PERSON{' '}
              <Text as="span" color={TRANSFER_COLORS.error}>
                required
              </Text>
            </Text>
            <Box mt={1}>
              <RecipientCombobox
                initialRecipients={data?.initialRecipients}
                onSearch={onSearch}
                selected={selected}
                onSelect={setSelected}
                isDisabled={busy}
                isInvalid={recipientError}
                errorId="transfer-recipient-error"
                debounceMs={searchDebounceMs}
              />
            </Box>
            {recipientError && (
              <Text
                data-testid="transfer-recipient-error"
                id="transfer-recipient-error"
                role="alert"
                color={TRANSFER_COLORS.error}
                textStyle="Body 1"
                mt={1}
              >
                Select a person to transfer this case to.
              </Text>
            )}
          </Box>

          {/* REASON — native checkboxes in a real fieldset/legend */}
          <Box
            as="fieldset"
            ref={reasonGroupRef}
            tabIndex={-1}
            aria-invalid={reasonError || undefined}
            aria-describedby={reasonError ? 'transfer-reason-error' : undefined}
            mb={6}
          >
            <Text as="legend" {...EYEBROW} display="block" mb={3}>
              REASON{' '}
              <Text as="span" color={TRANSFER_COLORS.error}>
                required
              </Text>{' '}
              <Text as="span" color={TRANSFER_COLORS.inkFaint} fontWeight={400}>
                select all that apply
              </Text>
            </Text>
            <ReasonField
              kind="transfer"
              idPrefix="transfer"
              mode="multi"
              state={reasonState}
              onRetry={retry}
              selectedKeys={reasonKeys}
              onChange={setReasonKeys}
              isDisabled={busy}
              palette={{
                chipSelected: TRANSFER_COLORS.chipSelected,
                border: TRANSFER_COLORS.border,
                inkSoft: TRANSFER_COLORS.inkSoft,
                inkMuted: TRANSFER_COLORS.inkFaint,
                error: TRANSFER_COLORS.error
              }}
            />
            {reasonError && (
              <Text
                data-testid="transfer-reason-error"
                id="transfer-reason-error"
                role="alert"
                color={TRANSFER_COLORS.error}
                textStyle="Body 1"
                mt={1}
              >
                Select at least one reason.
              </Text>
            )}
          </Box>

          {/* NOTE */}
          <Box mb={6}>
            <Text as="label" htmlFor="transfer-note-input" {...EYEBROW}>
              NOTE{' '}
              <Text as="span" color={TRANSFER_COLORS.inkFaint} fontWeight={400}>
                optional
              </Text>
            </Text>
            <Textarea
              id="transfer-note-input"
              data-testid="transfer-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              isDisabled={busy}
              maxLength={1024}
              placeholder="Add information about this transfer (optional)…"
              bg={TRANSFER_COLORS.inputBg}
              borderColor={TRANSFER_COLORS.border}
              borderRadius={CASE_ACTION_RADIUS.control}
              textStyle="Body 1"
              _hover={{ borderColor: TRANSFER_COLORS.noteHoverBorder }}
              _placeholder={{ color: TRANSFER_COLORS.inkFaint }}
              mt={1}
            />
          </Box>

          {/* ADD CONTEXT FOR THIS CASE — a SECOND, distinct optional field (ticket R7). The
              prototype merges the two; the ticket names them separately, so both ship. */}
          <Box>
            <Text as="label" htmlFor="transfer-context-input" {...EYEBROW}>
              ADD CONTEXT FOR THIS CASE{' '}
              <Text as="span" color={TRANSFER_COLORS.inkFaint} fontWeight={400}>
                optional
              </Text>
            </Text>
            <Textarea
              id="transfer-context-input"
              data-testid="transfer-context"
              value={caseContext}
              onChange={(e) => setCaseContext(e.target.value)}
              isDisabled={busy}
              maxLength={2000}
              placeholder="Add context that will help the receiving coder (optional)…"
              bg={TRANSFER_COLORS.inputBg}
              borderColor={TRANSFER_COLORS.border}
              borderRadius={CASE_ACTION_RADIUS.control}
              textStyle="Body 1"
              _hover={{ borderColor: TRANSFER_COLORS.noteHoverBorder }}
              _placeholder={{ color: TRANSFER_COLORS.inkFaint }}
              mt={1}
            />
          </Box>
        </Box>

        <ModalFooter
          alignItems="center"
          gap={4}
          px={7}
          sx={{ paddingTop: '1.125rem', paddingBottom: '1.125rem' }}
          borderTopWidth="1px"
          borderColor={TRANSFER_COLORS.border}
        >
          <LoggedAsLine
            testId="transfer-logged-as"
            name={loggedAs.name}
            at={loggedAs.at}
            timeZone={loggedAs.timeZone}
          />
          <Flex flex="1" gap={3} align="center" justify="flex-end" flexWrap="wrap">
            {submitState === 'error' && errorMessage && errs.length === 0 && (
              <Text
                data-testid="transfer-submit-error"
                role="alert"
                color={TRANSFER_COLORS.error}
                textStyle="Body 1"
              >
                {errorMessage}
              </Text>
            )}
            <AtomButton
              className="btn-secondary"
              data-testid="transfer-cancel"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </AtomButton>
            <AtomButton
              className="btn-primary"
              data-testid="transfer-submit"
              onClick={() => attempt()}
              isLoading={busy}
              disabled={busy || !canSubmit || noReasons}
            >
              Transfer Case
            </AtomButton>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
